import { type ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import Badge from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import {
  ACTIVITY_VIEW_TRIALS,
  buildActivityLink,
} from "@/v2-backoffice-client/signIn/signIn.constants"
import UsageScoreMeter from "@/v2-backoffice-client/usage/UsageScoreMeter"
import UsageSeatBar from "@/v2-backoffice-client/usage/scope/UsageSeatBar"
import {
  getScoreLevel,
  SCORE_LEVEL_LABELS,
} from "@/v2-backoffice-client/usage/usage.constants"
import type { UsageScopeSummary } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import type { BadgeVariant } from "@/v2-backoffice-client/components/badge"
import type { PortfolioAudience } from "@/v2-backoffice-client/portfolio/portfolio.audience.utils"
import type { PortfolioRevenueAtRisk } from "@/v2-backoffice-client/portfolio/portfolio.health.utils"
import type { PortfolioTrialPulse } from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioKpiCardsProps = ComponentProps<"section"> & {
  customerCount: number
  /** Customers at high or medium churn risk. */
  atRiskCount: number
  /** The 12-month billed revenue the churn signals put at stake. */
  revenueAtRisk: PortfolioRevenueAtRisk
  /** Trialing customers with their urgency split (overdue / ending soon). */
  trials: PortfolioTrialPulse
  /** The whole book of business rolled up as one scope. */
  fleet: UsageScopeSummary
  /** Fleet audience stats (DAU/WAU/MAU + stickiness). */
  audience: PortfolioAudience
}

/**
 * The habit read behind the stickiness ratio. Benchmarks are for a B2B
 * work tool: DAU counts weekends while the fleet works Mon–Fri, so even a
 * product used EVERY working day tops out near 5/7 ≈ 71% — 20%+ already
 * means a large share of the audience opens it most working days.
 */
function getStickinessRead(stickiness: number): {
  label: string
  variant: BadgeVariant
} {
  if (stickiness >= 0.35) return { label: "Daily habit", variant: "positive" }
  if (stickiness >= 0.2) return { label: "Strong habit", variant: "positive" }
  if (stickiness >= 0.1) return { label: "Weekly workflow", variant: "neutral" }
  return { label: "Occasional tool", variant: "warning" }
}

/** One decimal below 10 (where the fraction matters), integers above. */
function formatDau(value: number) {
  return value >= 10 ? formatNumber(Math.round(value)) : formatNumber(value, 1)
}

// Fleet-level KPI strip: the same numbers a customer's Activity tab leads
// with, aggregated across every customer-linked seat. Self-explanatory over
// raw ("812 of 1,240 seats active", not a bare count) per the house style.
// The window-following activity total is NOT here — it headlines the daily
// chart, where the trendline it summarizes actually lives.
function PortfolioKpiCards(props: PortfolioKpiCardsProps) {
  const {
    customerCount,
    atRiskCount,
    revenueAtRisk,
    trials,
    fleet,
    audience,
    className,
    ...sectionProps
  } = props

  const stickinessRead =
    audience.stickiness != null ? getStickinessRead(audience.stickiness) : null

  return (
    <section
      className={cn("usage-report-kpis usage-report-kpis--seven", className)}
      {...sectionProps}
    >
      <UsageReportKpi
        label="Customers"
        value={
          <>
            {formatNumber(customerCount)}
            <span className="truncate text-sm font-normal text-muted-foreground">
              with seats
            </span>
          </>
        }
        sub={
          atRiskCount === 0 ? (
            "none need attention right now"
          ) : (
            <>
              <span className="font-medium text-amber-500">
                {formatNumber(atRiskCount)}
              </span>{" "}
              need attention
            </>
          )
        }
      />

      {/* The money behind the churn signals: what the at-risk accounts
          billed the last 12 months — what a lost renewal actually costs,
          not a seat-count proxy. Same invoice mirror as the customer
          panel's Billing tab. */}
      <UsageReportKpi
        label="Revenue at risk"
        tone={revenueAtRisk.atRisk > 0 ? "warning" : "default"}
        value={
          revenueAtRisk.total === 0 ? (
            "—"
          ) : (
            <>
              <span
                className={cn(
                  revenueAtRisk.atRisk > 0 &&
                    "font-medium " +
                      (revenueAtRisk.high > 0
                        ? "text-destructive"
                        : "text-amber-500"),
                )}
              >
                {formatCompactAmount(
                  revenueAtRisk.atRisk,
                  revenueAtRisk.currency,
                )}
              </span>
              <span className="truncate text-sm font-normal text-muted-foreground">
                of {formatCompactAmount(revenueAtRisk.total, null)} billed
              </span>
            </>
          )
        }
        sub={
          revenueAtRisk.total === 0
            ? "no invoice history among the customers in scope"
            : revenueAtRisk.atRisk === 0
              ? "nothing billed rides on at-risk accounts"
              : [
                  `${Math.round((revenueAtRisk.atRisk / revenueAtRisk.total) * 100)} % of the last 12 months' billing sits on at-risk accounts`,
                  revenueAtRisk.high > 0
                    ? `${formatCompactAmount(revenueAtRisk.high, null)} of it at high risk`
                    : null,
                ]
        }
      />

      {/* The trial pipeline at a glance: overdue trials are unbilled usage
          (the fair-share leak this whole board exists to close), ending-soon
          ones are this week's conversion calls. Scope to them with the
          License status filter above. */}
      <UsageReportKpi
        label="Trials"
        tone={trials.overdue > 0 ? "warning" : "default"}
        value={
          <>
            {formatNumber(trials.count)}
            <span className="truncate text-sm font-normal text-muted-foreground">
              running
            </span>
          </>
        }
        sub={[
          trials.count === 0 ? (
            "no customers in trial right now"
          ) : trials.overdue === 0 && trials.endingSoon === 0 ? (
            "none ending in the next 30 days"
          ) : (
            <>
              {trials.overdue > 0 && (
                <span className="font-medium text-destructive">
                  {formatNumber(trials.overdue)} past end date
                </span>
              )}
              {trials.overdue > 0 && trials.endingSoon > 0 && " · "}
              {trials.endingSoon > 0 && (
                <span className="font-medium text-amber-500">
                  {formatNumber(trials.endingSoon)} ending soon
                </span>
              )}
            </>
          ),
          // The trials report: every trial license (the MCP pilots too, which
          // this count's governing status leaves out) with who uses it.
          <Link
            key="trials-report"
            to={buildActivityLink(ACTIVITY_VIEW_TRIALS)}
            iconSrc="arrow-right"
            iconSize={10}
            size="xs"
          >
            Open the trials report
          </Link>,
        ]}
      />

      <UsageReportKpi
        label="Seats in use"
        value={
          <>
            {formatNumber(fleet.activeUsers28d)}
            <span className="truncate text-sm font-normal text-muted-foreground">
              of {formatNumber(fleet.seatCount)}
            </span>
          </>
        }
        sub={[
          "active in the last 28 days",
          <span key="bar" className="block w-full pt-1">
            <UsageSeatBar bands={fleet.seatBands} />
          </span>,
        ]}
      />

      {/* The classic size trio in ONE tile — daily (mean distinct users
          per day), weekly, monthly. Monthly equals the seats-in-use
          numerator on purpose; this tile reads audience, that one reads
          seat utilization. */}
      <UsageReportKpi
        label="People on a day · week · month"
        value={
          <>
            {formatDau(audience.avgDau)}
            <span className="text-sm font-normal text-muted-foreground">
              / {formatNumber(audience.wau)} / {formatNumber(audience.mau)}
            </span>
          </>
        }
        sub={`daily average (peak ${formatNumber(audience.peakDau)}), a week, a month`}
      />

      {/* DAU ÷ MAU: how much of the monthly audience shows up on an
          average day. B2B benchmark language — DAU counts weekends, so a
          pure workweek product tops out near 5/7 ≈ 71%. */}
      <UsageReportKpi
        label="Stickiness"
        value={
          <>
            {audience.stickiness != null
              ? `${formatNumber(Math.round(audience.stickiness * 100))} %`
              : "—"}
            {stickinessRead && (
              <Badge size="xs" variant={stickinessRead.variant}>
                {stickinessRead.label}
              </Badge>
            )}
          </>
        }
        sub="of the month's people show up on an average day; a workweek tool tops out near 71 %"
      />

      <UsageReportKpi
        label="Engagement"
        value={
          <>
            {fleet.avgScore ?? "—"}
            <span className="truncate text-sm font-normal text-muted-foreground">
              {SCORE_LEVEL_LABELS[getScoreLevel(fleet.avgScore)]}
            </span>
          </>
        }
        sub={[
          "score of the active seats, out of 100",
          fleet.avgScore != null ? (
            <span key="meter" className="block w-full pt-1">
              <UsageScoreMeter score={fleet.avgScore} />
            </span>
          ) : (
            "no scored activity yet"
          ),
        ]}
      />
    </section>
  )
}

export default PortfolioKpiCards
